import React, {useEffect, useState} from 'react';
import {observer} from "mobx-react";
import BlockTitle from "../_BlockTitle";
import styles from "./styles/AccordionTerrace.module.scss"
import classNames from 'classnames';
import FontAwesome from "../../../utilities/FontAwesome";
import AccordionItemEditor from "./AccordionItemEditor";
import AccordionItem from "./AccordionItem";
import {EditDropDownItem} from "../EditDropDown";
import {AccordionItemSearchResultList} from "./AccordionItemSearchResultList";
import SearchBox from "../../../../pages/SearchPage/SearchBox";
import AccordionReorderingTheme from './AccordionReorderingTheme';
import {getUpdatedItemPathsFromMove, numExpandedNodes} from "../../../admin/tree/Tree";
import {runInAction, toJS} from "mobx";
import {convertFlatToTree, convertTreeToFlat} from "../../../../utils/DataUtilities";
import WatsonApi from "../../../../backends/WatsonApi";
import NotificationManager from "../../../notifications/NotificationManager";
import dynamic from "next/dynamic";
import {useStores} from "../../../../stores/useStores";

const SortableTreeWithoutDndContext = dynamic(() => import("@nosferatu500/react-sortable-tree"));

const AccordionTerrace = observer((props: {
    blockObj: IAccordionTerrace,
    aboveTheFold: boolean,
}) => {
    const {organizationStore} = useStores()
    const [reorder, setReorder] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [addingNewAccordionItem, setAddingNewAccordionItem] = useState(false);


    const accordionTerraceContainerClassName = classNames({
        [styles.container]: true,
    })
    const [expanded, setExpanded] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [previousSearchTerm, setPreviousSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    let accordionList = convertFlatToTree(props.blockObj.items) || [];

    const toggleExpansion = (index: number) => {
        if (expanded[index]) {
            setExpanded({...expanded, [index]: false})
        } else {
            setExpanded({...expanded, [index]: true})
        }
    }

    const expandAll = () => {
        setExpanded(accordionList.reduce((acc, item) => {
            acc[item.id] = true;
            return acc;
        }, {} as Record<string, boolean>));
    };

    const collapseAll = () => {
        setExpanded(accordionList.reduce((acc, item) => {
            acc[item.id] = false;
            return acc;
        }, {} as Record<string, boolean>));
    };

    const allAreExpanded = accordionList.every(item => expanded[item.id]);

    const reorderClassName = classNames({
        [styles.reorder]: reorder,
    });

    function _setEditing(value) {
        setCanEdit(value);
        setAddingNewAccordionItem(false);
        setReorder(false);
    }

    const handleCancel = () => {
        setAddingNewAccordionItem(false);
    }

    const reorderAccordionDropDownItem = {
        title: 'Toggle Reordering',
        icon: <FontAwesome prefix={"fas"} fixedWidth name={'fa-arrows-up-down'} ariaHidden
                           className={reorderClassName}/>,
        onClick: () => setReorder(v => !v),
        hideButtonSelector: true,
    }

    const accordionTerraceDropDownItems: EditDropDownItem[] = [
        {
            title: 'Add Message',
            icon: <FontAwesome prefix={'fas'} fixedWidth name={"fa-plus"}/>,
            onClick: () => setAddingNewAccordionItem(true),
        },
    ]

    if (accordionList?.length > 1) {
        accordionTerraceDropDownItems.push(reorderAccordionDropDownItem)
    }

    const handleSearch = async () => {
        if (searchTerm==='') return;
        setIsSearching(true);
        setIsLoading(true)
        try {
            const client = await WatsonApi();
            const response = await client.apis.organizations.organizations_search_list({
                organization_pk: organizationStore.organization.id,
                query: searchTerm,
                limit_results: "objects",
                "objects.content_type": "accordionitems",
                "objects.page_id": props.blockObj.organization_id,
            });
            const parsed = JSON.parse(response.data);
            setSearchResults(parsed.objects.data);
            setPreviousSearchTerm(searchTerm);
        }
        catch (e) {
            console.error(e);
        }
        setIsLoading(false)
    }

    const ExpandAllButton = observer((props: {
        text: string,
        toggleAll?: () => void,
    }) => {


        return <button className={styles.expandAllButtonContainer} onClick={props.toggleAll}>
            <span>{props.text}</span>
        </button>
    })

    const [height, setHeight] = useState(accordionList.length * 50);

    useEffect(() => {
        setHeight(accordionList.length * 50);
    }, [accordionList.length]);

    const searchAndExpandButtonStyles = classNames({
        [styles.searchAndExpandButtonContainer]: true,
        [styles.reorderActive]: reorder,
    })

    const handleMoveNode = async (obj) => {
        const previousFlatState: IAccordionItem[] = toJS(props.blockObj.items) || [];
        try {
            const nextFlatState: IAccordionItem[] = convertTreeToFlat(obj.treeData, "");
            const updatedItems: IAccordionItem[] = getUpdatedItemPathsFromMove(previousFlatState, nextFlatState);
            if (updatedItems.length === 0) return;
            if (updatedItems.filter(item=> item.path.includes('.')).length > 0 && !organizationStore.organization?.billing_products?.docmersion) {
                NotificationManager.error("You must have Docmersion to create subitem accordion items.")
                return;
            }

            const client = await WatsonApi();
            await client.apis.organizations.organizations_terraces_accordion_items_bulk_partial_update({
                organization_pk: props.blockObj.organization_id,
                terrace_pk: props.blockObj.id,
                data: updatedItems.map(item => ({ id: item.id, path: item.path })),
            });

            runInAction(() => {
                props.blockObj.items = nextFlatState;
            })
            NotificationManager.success("Accordion Terrace successfully reordered!")
        } catch (e) {
            NotificationManager.error(e.message)
            runInAction(() => {
                props.blockObj.items = previousFlatState;
            })
        }
    }

    const searchAndExpandButton = <div className={searchAndExpandButtonStyles}>
        {organizationStore.organization.billing_products?.docmersion && <div className={styles.searchBoxContainer}>
            <SearchBox
            searchTerm={previousSearchTerm}
            placeholder={`Search ${props.blockObj.title}`}
            isValidating={false}
            inputValue={searchTerm}
            setInputValue={setSearchTerm}
            handleSubmit={handleSearch}
        />
        </div>}
        {accordionList.length > 1 && !isSearching && !isSearchExpanded ? <ExpandAllButton text={allAreExpanded ? "Collapse All" : "Expand All"}
                                                                                          toggleAll={allAreExpanded ? collapseAll : expandAll}/> : null}
    </div>


    return (
        <article>
            <BlockTitle
                blockObj={props.blockObj}
                aboveTheFold={props.aboveTheFold}
                editing={canEdit}
                setEditing={_setEditing}
                allowSave={true}
                dropDownItems={accordionTerraceDropDownItems}
                showViewMore={false}
                rightElement={searchAndExpandButton}
                rightElementClassName={styles.searchAndButtonContainer}
            />
            <div className={accordionTerraceContainerClassName}>
                {!reorder ? <>{isSearching && <AccordionItemSearchResultList results={searchResults} setIsSearching={setIsSearching} searchTerm={searchTerm} setSearchTerm={setSearchTerm} previousSearchTerm={previousSearchTerm} setIsExpanded={setIsSearchExpanded} isLoading={isLoading}/>}
                    {!isSearching && accordionList?.length > 0 && !addingNewAccordionItem && <ul className={styles.accordionList}>
                        {accordionList?.map((accordionItem) => {
                        const itemIndex = props.blockObj?.items?.findIndex(item => item.id === accordionItem.id);
                        return <AccordionItem key={accordionItem.id}
                                              totalItemCount={props.blockObj?.items?.length || 0}
                                              getItemAtIndex={idx => {
                                                  return props.blockObj.items.length >= idx ? props.blockObj.items[idx] : null;
                                              }}
                                              initialItemIndex={itemIndex}
                                              item={accordionItem}
                                              accordionList={accordionList}
                                              index={itemIndex}
                                              blockObj={props.blockObj}
                                              handleCancel={handleCancel}
                                              expanded={expanded[accordionItem.id]}
                                              setExpanded={setExpanded}
                                              toggleExpansion={toggleExpansion}
                        />
                    })}
                    </ul>
                }</> :
                <SortableTreeWithoutDndContext
                    style={{height: height, backgroundColor: 'white'}}
                    treeData={organizationStore.organization.billing_products?.docmersion ? accordionList :
                accordionList.map(({children, ...rest}) => rest)}
                    onChange={() => {
                    }}
                    onVisibilityToggle={({treeData, node, expanded}) => {
                        props.blockObj.items = props.blockObj.items.map((n: IAccordionItem): IAccordionItem => {
                            if (n.id === node.id) {
                                n.expanded = expanded;
                            }
                            return n;
                        })
                        setHeight(numExpandedNodes(treeData) * 60 + 20);
                    }}
                    onMoveNode={handleMoveNode}
                    theme={AccordionReorderingTheme}
                    rowHeight={60}
                    scaffoldBlockPxWidth={30}
                    getNodeKey={({node}) => node.id}
                    maxDepth={organizationStore.organization?.billing_products?.docmersion ? 2 : 1}
                />}
                {!isSearching && addingNewAccordionItem &&
                    <div className={styles.accordionItem}>
                        <AccordionItemEditor
                            blockObj={props.blockObj}
                            index={accordionList?.length || 0}
                            handleCancel={handleCancel}
                            accordionList={accordionList}
                        />
                    </div>
                }
            </div>
        </article>
    );
})

export default AccordionTerrace;
